import { type FormEvent, useEffect, useMemo, useState } from "react"
import styled from "styled-components"

import useAuthStore from "@features/auth/authStore.client"
import { ActionLink, EmptyState, ErrorNotice, PanelEyebrow, PrimaryButton } from "@features/portal/PortalPrimitives"
import { serviceRequestsApi } from "@features/portal/serviceRequestsApi"
import { categoryLabel, normalizePortalRole, statusLabel, type ServiceRequest, type ServiceRequestStatus } from "@features/portal/types"
import PortalShell from "../Common/PortalShell"

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 1.25rem;
`

const FilterButton = styled.button<{ $active: boolean }>`
  min-height: 42px;
  padding: 0 1rem;
  border: 1px solid ${({ $active }) => ($active ? "#1d6da6" : "#bdcad5")};
  border-radius: 2px;
  background: ${({ $active }) => ($active ? "#0d4172" : "#ffffff")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#173957")};
  font: inherit;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;
`

const RequestList = styled.div`
  display: grid;
  gap: 1rem;
`

const RequestCard = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 0.36fr);
  gap: clamp(1.25rem, 4vw, 3rem);
  padding: clamp(1.25rem, 3vw, 2rem);
  border: 1px solid #c4d0da;
  border-left: 4px solid #337abd;
  border-radius: 2px;
  background: #ffffff;
  box-shadow: 0 16px 45px rgba(10, 43, 70, 0.05);

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`

const RequestHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;

  h2 {
    margin: 0;
    color: #102c47;
    font-size: clamp(1.25rem, 3vw, 1.75rem);
    letter-spacing: -0.025em;
  }

  @media (max-width: 520px) {
    flex-direction: column;
  }
`

const Status = styled.span<{ $status: ServiceRequestStatus }>`
  flex: 0 0 auto;
  padding: 0.38rem 0.6rem;
  border: 1px solid ${({ $status }) => ($status === "TERMINEE" ? "#82aa91" : "#8ab8d9")};
  background: ${({ $status }) => ($status === "TERMINEE" ? "#eef7f1" : "#eef7fd")};
  color: ${({ $status }) => ($status === "TERMINEE" ? "#2f6643" : "#245d88")};
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const Meta = styled.p`
  margin: 0.55rem 0 0;
  color: #687d8f;
  font-size: 0.85rem;
  line-height: 1.55;
`

const Description = styled.p`
  margin: 1.2rem 0 0;
  color: #2f465b;
  line-height: 1.7;
  white-space: pre-line;
`

const Attachments = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 1.2rem;

  img {
    width: 96px;
    height: 78px;
    object-fit: cover;
    border: 1px solid #c4d0da;
    border-radius: 2px;
  }
`

const Followup = styled.aside`
  padding-left: 1.4rem;
  border-left: 1px solid #d2dbe3;

  @media (max-width: 760px) {
    padding: 1.25rem 0 0;
    border-top: 1px solid #d2dbe3;
    border-left: 0;
  }
`

const Message = styled.div`
  margin: 0.7rem 0;
  padding: 0.75rem;
  background: #f0f5f8;
  color: #334d63;
  font-size: 0.82rem;
  line-height: 1.5;

  strong {
    display: block;
    margin-bottom: 0.18rem;
    color: #1b4d76;
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
`

const MessageForm = styled.form`
  display: grid;
  gap: 0.65rem;
  margin-top: 1rem;

  textarea,
  select {
    width: 100%;
    min-height: 45px;
    padding: 0.75rem;
    border: 1px solid #b9c7d2;
    border-radius: 2px;
    background: #ffffff;
    color: #153650;
    font: inherit;
    font-size: 0.85rem;
  }

  textarea {
    min-height: 92px;
    resize: vertical;
  }
`

type Filter = "TOUTES" | "OUVERTES" | "TERMINEE"

const Requests = () => {
  const user = useAuthStore((state) => state.user)
  const role = normalizePortalRole(user?.role)
  const [requests, setRequests] = useState<ServiceRequest[]>([])
  const [filter, setFilter] = useState<Filter>("OUVERTES")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [messageById, setMessageById] = useState<Record<string, string>>({})
  const [busyId, setBusyId] = useState("")

  useEffect(() => {
    serviceRequestsApi
      .list()
      .then(setRequests)
      .catch(() => setError("Impossible de charger les demandes pour le moment."))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(
    () =>
      requests.filter((request) => {
        if (filter === "TOUTES") return true
        if (filter === "TERMINEE") return request.status === "TERMINEE"
        return request.status !== "TERMINEE"
      }),
    [filter, requests],
  )

  const replaceRequest = (updated: ServiceRequest) => {
    const updatedId = updated._id || updated.id
    setRequests((current) => current.map((item) => ((item._id || item.id) === updatedId ? updated : item)))
  }

  const updateStatus = async (request: ServiceRequest, status: ServiceRequestStatus) => {
    const id = request._id || request.id
    if (!id) return
    setBusyId(id)
    setError("")
    try {
      replaceRequest(await serviceRequestsApi.updateStatus(id, status))
    } catch {
      setError("Le statut n’a pas pu être mis à jour.")
    } finally {
      setBusyId("")
    }
  }

  const sendMessage = async (event: FormEvent, request: ServiceRequest) => {
    event.preventDefault()
    const id = request._id || request.id
    const text = id ? messageById[id]?.trim() : ""
    if (!id || !text) return
    setBusyId(id)
    setError("")
    try {
      replaceRequest(await serviceRequestsApi.addMessage(id, text))
      setMessageById((current) => ({ ...current, [id]: "" }))
    } catch {
      setError("Le message n’a pas pu être envoyé.")
    } finally {
      setBusyId("")
    }
  }

  return (
    <PortalShell
      title={role === "LOCATAIRE" ? "Vos demandes" : "Demandes de service"}
      eyebrow="Suivi"
      action={<ActionLink to="/app/nouvelle-demande">Nouvelle demande</ActionLink>}
    >
      <FilterBar aria-label="Filtrer les demandes">
        {(
          [
            ["OUVERTES", "En cours"],
            ["TOUTES", "Toutes"],
            ["TERMINEE", "Terminées"],
          ] as const
        ).map(([value, label]) => (
          <FilterButton key={value} $active={filter === value} onClick={() => setFilter(value)}>
            {label}
          </FilterButton>
        ))}
      </FilterBar>

      {error && <ErrorNotice role="alert">{error}</ErrorNotice>}
      {loading ? (
        <EmptyState>Chargement des demandes…</EmptyState>
      ) : filtered.length ? (
        <RequestList>
          {filtered.map((request) => {
            const id = request._id || request.id || request.createdAt
            return (
              <RequestCard key={id}>
                <div>
                  <RequestHeader>
                    <div>
                      <PanelEyebrow>{categoryLabel[request.category]}</PanelEyebrow>
                      <h2>{request.propertyLabel || "Demande de service"}</h2>
                    </div>
                    <Status $status={request.status}>{statusLabel[request.status]}</Status>
                  </RequestHeader>
                  <Meta>
                    {request.unit ? `Unité ${request.unit} · ` : ""}
                    {new Intl.DateTimeFormat("fr-CA", { dateStyle: "long" }).format(new Date(request.createdAt))}
                    {role === "ADMIN" ? ` · ${request.requesterName}` : ""}
                  </Meta>
                  <Description>{request.description}</Description>
                  {request.availability && <Meta>Disponibilités : {request.availability}</Meta>}
                  {request.attachments?.length > 0 && (
                    <Attachments aria-label="Photos jointes">
                      {request.attachments.map((attachment, index) =>
                        attachment.url ? (
                          <a href={attachment.url} target="_blank" rel="noreferrer" key={`${attachment.name}-${index}`}>
                            <img src={attachment.url} alt={`Photo jointe ${index + 1} — ${attachment.name}`} />
                          </a>
                        ) : null,
                      )}
                    </Attachments>
                  )}
                </div>

                <Followup>
                  <PanelEyebrow>Suivi du dossier</PanelEyebrow>
                  {role === "ADMIN" && (
                    <MessageForm as="div">
                      <label htmlFor={`status-${id}`}>Statut</label>
                      <select
                        id={`status-${id}`}
                        value={request.status}
                        disabled={busyId === id}
                        onChange={(event) => updateStatus(request, event.target.value as ServiceRequestStatus)}
                      >
                        {Object.entries(statusLabel).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </MessageForm>
                  )}
                  {request.messages?.map((message, index) => (
                    <Message key={`${message.createdAt}-${index}`}>
                      <strong>{message.senderRole === "ADMIN" ? "Équipe TB" : "Message"}</strong>
                      {message.text}
                    </Message>
                  ))}
                  <MessageForm onSubmit={(event) => sendMessage(event, request)}>
                    <label htmlFor={`message-${id}`}>Ajouter un message</label>
                    <textarea
                      id={`message-${id}`}
                      value={messageById[id] || ""}
                      onChange={(event) => setMessageById((current) => ({ ...current, [id]: event.target.value }))}
                      placeholder="Ajoutez une précision utile…"
                      maxLength={1200}
                    />
                    <PrimaryButton type="submit" disabled={busyId === id || !messageById[id]?.trim()}>
                      {busyId === id ? "Envoi…" : "Envoyer"}
                    </PrimaryButton>
                  </MessageForm>
                </Followup>
              </RequestCard>
            )
          })}
        </RequestList>
      ) : (
        <EmptyState>Aucune demande ne correspond à ce filtre.</EmptyState>
      )}
    </PortalShell>
  )
}

export default Requests
